import React, { useEffect, useState, useMemo } from "react";
import { NodeData, Task as KanbanTask } from "../types/types";
import type { Node } from "@xyflow/react";
import { SelectButton } from "primereact/selectbutton";
import { Gantt, Task, ViewMode } from "gantt-task-react";
import KanbanCard from "./KanbanCard";
import { stripHtmlFromLabel } from "../utils/textFormatting";

// Helper to get CSS variable value
const getCSSVar = (varName: string): string => {
  if (typeof document === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

interface TimeLineAreaProps {
  nodes: Node<NodeData>[];
  handleAddUpdateEvent: (
    id: string,
    nodeTitle: string,
    title: string,
    description: string,
    date: Date | "",
  ) => void;
  handleDeleteEvent: (id: string) => void;
  handleAddUpdateTask: (
    id: string,
    title: string,
    nodeTitle: string,
    status: string,
    summary: string,
    priority: string,
    tags: string,
    assignee: string,
    estimate: number,
    rankId: number,
    color: string,
    className: string,
    startDate: Date | null,
    endDate: Date | null,
  ) => void;
  mapTemplate: any;
}

const TimeLineArea: React.FC<TimeLineAreaProps> = ({
  nodes,
  handleAddUpdateEvent,
  handleDeleteEvent,
  handleAddUpdateTask,
  mapTemplate,
}: TimeLineAreaProps) => {
  const [nodeTasks, setNodeTasks] = useState<KanbanTask[]>([]);
  const [showTaskCard, setShowTaskCard] = useState(false);
  const [task, setTask] = useState<KanbanTask | null>(null);
  const [ganttViewMode, setGanttViewMode] = useState<ViewMode>(ViewMode.Week);
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    // Extract tasks from nodes that have task data and include parent info
    const tasks: KanbanTask[] = nodes
      .filter((node) => node.type !== "center" && node.data.task?.id)
      .map((node) => {
        // Find parent node to get parent label
        const parentNode = nodes.find((n) => n.id === node.parentId);

        const nodeLabelPlain = stripHtmlFromLabel(node.data.label || "");
        const parentLabelPlain = stripHtmlFromLabel(
          parentNode?.data?.label || "No Parent",
        );

        return {
          id: node.id,
          // Display label (plain text)
          title: nodeLabelPlain,
          // Keep original label for stable identity/updates
          nodeTitle: node.data.label,
          parentId: node.parentId,
          parentLabel: parentLabelPlain,
          status: node.data.task?.status || "Open",
          summary: node.data.task?.summary || "",
          priority: node.data.task?.priority || "",
          tags: node.data.task?.tags || "",
          assignee: node.data.task?.assignee || "",
          estimate: node.data.task?.estimate || 0,
          rankId: node.data.task?.rankId || 0,
          color: node.data.task?.color,
          className: node.data.task?.className,
          startDate: node.data.task?.startDate
            ? new Date(node.data.task.startDate)
            : undefined,
          endDate: node.data.task?.endDate
            ? new Date(node.data.task.endDate)
            : undefined,
        };
      });

    setNodeTasks(tasks);
  }, [nodes]);

  // Convert tasks to Gantt tasks with project grouping
  const ganttTasks: Task[] = useMemo(() => {
    // Find the root node
    const rootNode = nodes.find(
      (node) => node.type === "center" || node.id === "root",
    );
    const rootLabel = stripHtmlFromLabel(rootNode?.data?.label || "Root");

    // Group tasks by parentId (not label) to avoid HTML/duplication issues.
    const groupedByParentId = nodeTasks.reduce(
      (acc, task) => {
        const parentId = (task as any).parentId || "no-parent";
        const parentLabel = (task as any).parentLabel || "No Parent";
        if (!acc[parentId]) {
          acc[parentId] = { label: parentLabel, tasks: [] as KanbanTask[] };
        }
        acc[parentId].tasks.push(task);
        return acc;
      },
      {} as Record<string, { label: string; tasks: KanbanTask[] }>,
    );

    const result: Task[] = [];

    // Calculate overall timeline from all tasks
    const allStartDates = nodeTasks
      .filter((t) => t.startDate)
      .map((t) => new Date(t.startDate!).getTime());
    const allEndDates = nodeTasks
      .filter((t) => t.endDate)
      .map((t) => new Date(t.endDate!).getTime());

    const rootStart =
      allStartDates.length > 0
        ? new Date(Math.min(...allStartDates))
        : new Date();
    const rootEnd =
      allEndDates.length > 0 ? new Date(Math.max(...allEndDates)) : new Date();

    // Add root node as the top-level project
    const rootProjectId = "project-root";
    result.push({
      id: rootProjectId,
      name: rootLabel,
      start: rootStart,
      end: rootEnd,
      progress: 0,
      type: "project" as const,
      hideChildren: collapsedProjects.has(rootProjectId),
      styles: {
        progressColor: "#10b981",
        progressSelectedColor: "#059669",
        backgroundColor: "#94a3b8",
        backgroundSelectedColor: "#64748b",
      },
    });

    // Create a project (parent) and its tasks
    Object.entries(groupedByParentId).forEach(([parentId, group]) => {
      const parentLabel = group.label;
      const tasks = group.tasks;
      // Add parent as a project
      const projectStart = new Date(
        Math.min(
          ...tasks.map((t) =>
            t.startDate ? new Date(t.startDate).getTime() : Date.now(),
          ),
        ),
      );
      const projectEnd = new Date(
        Math.max(
          ...tasks.map((t) =>
            t.endDate ? new Date(t.endDate).getTime() : Date.now(),
          ),
        ),
      );

      const projectId = `project-${parentId}`;
      result.push({
        id: projectId,
        name: parentLabel,
        start: projectStart,
        end: projectEnd,
        progress: 0,
        type: "project" as const,
        project: rootProjectId, // Make this a child of root
        hideChildren: collapsedProjects.has(projectId),
        styles: {
          progressColor: "#10b981",
          progressSelectedColor: "#059669",
          backgroundColor: "#cbd5e1",
          backgroundSelectedColor: "#94a3b8",
        },
      });

      // Add child tasks
      tasks.forEach((task) => {
        const startDate = task.startDate
          ? new Date(task.startDate)
          : new Date();
        const endDate = task.endDate
          ? new Date(task.endDate)
          : new Date(startDate.getTime() + 86400000);

        result.push({
          id: task.id.toString(),
          name: task.title,
          start: startDate,
          end: endDate,
          progress:
            task.status === "Done"
              ? 100
              : task.status === "InProgress"
                ? 50
                : 0,
          type: "task" as const,
          project: projectId,
          styles: {
            progressColor: "#3b82f6",
            progressSelectedColor: "#2563eb",
            backgroundColor: "#60a5fa",
            backgroundSelectedColor: "#3b82f6",
          },
        });
      });
    });

    return result;
  }, [nodeTasks, collapsedProjects]);

  // Filter tasks for display (hide children of collapsed projects)
  const visibleGanttTasks = useMemo(() => {
    // Helper to check if any ancestor is collapsed
    const isAnyAncestorCollapsed = (
      taskProjectId: string | undefined,
    ): boolean => {
      if (!taskProjectId) return false;
      if (collapsedProjects.has(taskProjectId)) return true;

      // Find the parent project and check its ancestors
      const parentProject = ganttTasks.find((t) => t.id === taskProjectId);
      if (parentProject?.project) {
        return isAnyAncestorCollapsed(parentProject.project);
      }

      return false;
    };

    return ganttTasks.filter((task) => {
      // Always show root project
      if (task.id === "project-root") return true;

      // For other projects and tasks, check if any ancestor is collapsed
      if (task.project && isAnyAncestorCollapsed(task.project)) {
        return false;
      }

      return true;
    });
  }, [ganttTasks, collapsedProjects]);

  const handleExpanderClick = (task: Task) => {
    setCollapsedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(task.id)) {
        next.delete(task.id);
      } else {
        next.add(task.id);
      }
      return next;
    });
  };

  const ganttViewOptions = [
    { label: "Hour", value: ViewMode.Hour },
    { label: "Day", value: ViewMode.Day },
    { label: "Week", value: ViewMode.Week },
    { label: "Month", value: ViewMode.Month },
  ];

  // Compatibility wrapper for handleAddUpdateTask
  const handleTaskUpdate = (
    id: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date,
  ) => {
    handleAddUpdateTask(
      id.toString(),
      title,
      nodeTitle,
      status,
      summary,
      priority || "",
      tags || "",
      assignee || "",
      estimate || 0,
      rankId || 0,
      color || "",
      className || "",
      startDate || null,
      endDate || null,
    );
    setTask(null);
    setShowTaskCard(false);
  };

  const handleTaskDelete = (id: string) => {
    handleDeleteEvent(id);
    setTask(null);
    setShowTaskCard(false);
  };

  // Handle date changes when dragging tasks in Gantt
  const handleDateChange = (task: Task) => {
    // Find the corresponding node task
    const nodeTask = nodeTasks.find((t) => t.id === task.id);
    if (nodeTask && task.type === "task") {
      // Update the task with new dates
      handleTaskUpdate(
        task.id,
        task.name,
        nodeTask.nodeTitle,
        nodeTask.status,
        nodeTask.summary || "",
        nodeTask.priority,
        nodeTask.tags,
        nodeTask.assignee,
        nodeTask.estimate,
        nodeTask.rankId,
        nodeTask.color,
        nodeTask.className,
        task.start,
        task.end,
      );
    }
  };

  // Handle progress changes when dragging progress bar
  const handleProgressChange = (task: Task) => {
    // Find the corresponding node task
    const nodeTask = nodeTasks.find((t) => t.id === task.id);
    if (nodeTask && task.type === "task") {
      // Determine status based on progress
      const newStatus =
        task.progress === 100
          ? "Done"
          : task.progress > 0
            ? "InProgress"
            : "Open";

      // Update the task with new progress/status
      handleTaskUpdate(
        task.id,
        task.name,
        nodeTask.nodeTitle,
        newStatus,
        nodeTask.summary || "",
        nodeTask.priority,
        nodeTask.tags,
        nodeTask.assignee,
        nodeTask.estimate,
        nodeTask.rankId,
        nodeTask.color,
        nodeTask.className,
        task.start,
        task.end,
      );
    }
  };

  return (
    <>
      <div
        className="timeline-container"
        style={{
          width: "100%",
          height: "100vh",
          paddingTop: "20px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            padding: "20px",
            paddingTop: "70px",
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: getCSSVar("--focusmap-surface") || "white",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          {/* <h2 style={{ margin: 0, color: "#136656" }}>Timeline Gantt View</h2> */}
          <SelectButton
            value={ganttViewMode}
            onChange={(e) => setGanttViewMode(e.value)}
            options={ganttViewOptions}
          />
        </div>

        {nodeTasks.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "80vh",
              width: "100%",
            }}
          >
            <h1
              style={{
                textAlign: "center",
                color: getCSSVar("--focusmap-text-muted"),
              }}
            >
              Mark items in your map view as tasks to act on them here
            </h1>
          </div>
        ) : (
          <div
            className="card"
            style={{
              padding: "20px",
              overflow: "auto",
              minHeight: "calc(100vh - 200px)",
              width: "100%",
            }}
          >
            <Gantt
              tasks={visibleGanttTasks}
              viewMode={ganttViewMode}
              onDoubleClick={(task) => {
                // Only open editor for actual tasks, not projects
                if (task.type === "task") {
                  const foundTask = nodeTasks.find((t) => t.id === task.id);
                  if (foundTask) {
                    setTask(foundTask);
                    setShowTaskCard(true);
                  }
                }
              }}
              onDateChange={handleDateChange}
              onProgressChange={handleProgressChange}
              listCellWidth="430px"
              columnWidth={
                ganttViewMode === ViewMode.Month
                  ? 300
                  : ganttViewMode === ViewMode.Week
                    ? 120
                    : ganttViewMode === ViewMode.Day
                      ? 65
                      : 60
              }
              locale="en-US"
              TaskListHeader={({ headerHeight }) => {
                return (
                  <div
                    style={{
                      display: "flex",
                      height: headerHeight,
                      fontFamily: "Arial, Roboto, sans-serif",
                      fontSize: "14px",
                      borderBottom: "1px solid #e0e0e0",
                      borderRight: "1px solid #e0e0e0",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        minWidth: 250,
                        flex: "1 1 auto",
                        padding: "5px 10px",
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                      }}
                    >
                      Name
                    </div>
                    <div
                      style={{
                        width: 90,
                        minWidth: 90,
                        maxWidth: 90,
                        padding: "5px 10px",
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      From
                    </div>
                    <div
                      style={{
                        width: 90,
                        minWidth: 90,
                        maxWidth: 90,
                        padding: "5px 10px",
                        display: "flex",
                        justifyContent: "start",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      To
                    </div>
                  </div>
                );
              }}
              TaskListTable={({
                tasks,
                rowHeight,
                rowWidth,
                onExpanderClick,
              }) => {
                const formatShortDate = (date: Date) => {
                  return date.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "2-digit",
                  });
                };

                // Filter tasks based on hideChildren state
                const visibleTasks = tasks.filter((task) => {
                  // Always show project rows
                  if (task.type === "project") return true;

                  // For child tasks, check if parent is collapsed
                  if (task.project) {
                    const parentTask = tasks.find((t) => t.id === task.project);
                    return !parentTask?.hideChildren;
                  }

                  return true;
                });

                return (
                  <div
                    style={{
                      fontFamily: "Arial, Roboto, sans-serif",
                      fontSize: "14px",
                      borderRight: "1px solid #e0e0e0",
                      width: "100%",
                    }}
                  >
                    {visibleTasks.map((task) => {
                      const isProject = task.type === "project";
                      const isRootProject = task.id === "project-root";
                      const isChildProject = isProject && !isRootProject;

                      // Calculate indentation
                      let indentLevel = 0;
                      if (!isProject) {
                        indentLevel = 2; // Tasks are indented twice
                      } else if (isChildProject) {
                        indentLevel = 1; // Child projects are indented once
                      }

                      return (
                        <div
                          key={task.id}
                          style={{
                            display: "flex",
                            height: rowHeight,
                            width: "100%",
                            // borderBottom: "1px solid #e0e0e0",
                            borderRight: "1px solid #f0f0f0",
                          }}
                        >
                          <div
                            style={{
                              minWidth: 250,
                              flex: "1 1 auto",
                              padding: "5px 10px",
                              display: "flex",
                              alignItems: "center",
                              borderBottom: "1px solid #f0f0f0",
                              // borderRight: "1px solid #e0e0e0",
                              cursor: isProject ? "pointer" : "default",
                              paddingLeft: `${10 + indentLevel * 20}px`,
                            }}
                            onClick={() =>
                              isProject && handleExpanderClick(task)
                            }
                          >
                            {isProject && (
                              <span style={{ marginRight: "5px" }}>
                                {task.hideChildren ? "▸" : "▾"}
                              </span>
                            )}
                            <span
                              style={{
                                fontWeight: isProject ? "bold" : "normal",
                              }}
                            >
                              {task.name}
                            </span>
                          </div>
                          <div
                            style={{
                              width: 90,
                              minWidth: 90,
                              maxWidth: 90,
                              padding: "5px 10px",
                              display: "flex",
                              alignItems: "center",
                              borderBottom: "1px solid #f0f0f0",
                              borderLeft: "1px solid #f0f0f0",
                              borderRight: "1px solid #f0f0f0",
                              flexShrink: 0,
                            }}
                          >
                            {formatShortDate(task.start)}
                          </div>
                          <div
                            style={{
                              width: 90,
                              minWidth: 90,
                              maxWidth: 90,
                              padding: "5px 10px",
                              display: "flex",
                              alignItems: "center",
                              borderBottom: "1px solid #f0f0f0",
                              borderRight: "1px solid #f0f0f0",
                              flexShrink: 0,
                            }}
                          >
                            {formatShortDate(task.end)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
              TooltipContent={({ task }) => {
                const formatDate = (date: Date) => {
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
                };
                return (
                  <div
                    className="gantt-tooltip-right"
                    style={{
                      padding: "12px",
                      backgroundColor: "#ffffff",
                      borderRadius: "6px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                      border: "1px solid #e0e0e0",
                      minWidth: "200px",
                    }}
                  >
                    <div>
                      <strong>{task.name}</strong>
                    </div>
                    <div>From: {formatDate(task.start)}</div>
                    <div>To: {formatDate(task.end)}</div>
                    {task.progress !== undefined && (
                      <div>Progress: {task.progress}%</div>
                    )}
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>

      {showTaskCard && task && (
        <KanbanCard
          visible={showTaskCard}
          nodeTask={task}
          setShowKanbanCard={setShowTaskCard}
          handleAddUpdateTask={handleTaskUpdate}
          handleDeleteTask={handleTaskDelete}
        />
      )}
    </>
  );
};

export default TimeLineArea;
